<template>

    <div class="card ui02-welcome mb-0">
        <!-- Header della card: avatar + saluto + solo nome. -->
        <div class="ui02-welcome-header">
            <span class="ui02-welcome-avatar" aria-hidden="true">
                <img v-if="avatarUrl && !avatarFallito" :src="avatarUrl"
                     class="ui02-welcome-avatar-img" alt=""
                     @error="avatarFallito = true" />
                <i v-else class="ph ph-user"></i>
            </span>
            <div class="ui02-welcome-title">
                <span class="ui02-welcome-saluto"><Translate :path="'dizionario.base.WELCOME'"></Translate>,</span>
                <span class="ui02-welcome-nome">{{ nomeVisibile }}</span>
            </div>
        </div>

        <!-- Body separato: testo + azione + decorazione sobria a destra. -->
        <div class="ui02-welcome-body">
            <div class="ui02-welcome-text">
                <p class="ui02-welcome-desc">
                    Panoramica delle attività e dell'andamento dei progetti nel periodo selezionato. Qui trovi i principali indicatori economici, lo stato dei progetti e le tue attività personali.
                </p>
                <div class="ui02-welcome-actions">
                    <button v-for="a in azioni" :key="a.nome" type="button" class="ui02-welcome-action"
                            @click="vai(a.rotta)">
                        <i :class="a.icona" aria-hidden="true"></i>{{ a.nome }}
                    </button>
                </div>
            </div>
            <!-- Decorazione sobria: icone Phosphor gia' presenti + forme pastel. -->
            <div class="ui02-welcome-art" aria-hidden="true">
                <span class="ui02-art-bolla b1"></span>
                <span class="ui02-art-bolla b2"></span>
                <span class="ui02-art-tile t1"><i class="ph ph-chart-bar"></i></span>
                <span class="ui02-art-tile t2"><i class="ph ph-calendar"></i></span>
                <span class="ui02-art-tile t3"><i class="ph ph-users-three"></i></span>
            </div>
        </div>
    </div>
    
</template>

<script>
/* UI-02B - Welcome con header/body separati. Solo il NOME dell'utente (mai
   nome+cognome): la derivazione avviene nel parent (DashboardManager) dalla
   sessione; qui c'e' solo il fallback neutro. Azioni solo se la route esiste
   davvero (qui: /Progetti, verificata nel router del progetto). */
export default {
    name: "WelcomeCard",

    props: {
        /* Solo nome utente, gia' derivato dal parent (fallback: vuoto). */
        nome: { type: String, default: "" },
        /* Avatar dell'utente loggato: URL gia' risolto dal parent dalla sessione.
           Vuoto = nessuna foto: la card ripiega sull'icona. */
        avatar: { type: String, default: "" },
    },

    data() {
        /* Flag di fallback: se l'<img> non carica (URL 404), si mostra l'icona.
           Si resetta quando l'avatar cambia (es. dopo upload). */
        return { avatarFallito: false };
    },

    watch: {
        avatar() { this.avatarFallito = false; },
        '$root.utente.URL_AVATAR'() { this.avatarFallito = false; },
    },

    computed: {
        /* UI-03 (chirurgia 1bis): URL avatar risolto qui via $root.filters
           (App.vue espone filters: global.filters in data, quindi disponibile
           nei figli come this.$root.filters). Mai 'filters' diretto nel
           template: non esposto a questo componente -> crash runtime. Guard
           completa: nessun crash se filters/utente/URL_AVATAR mancano. */
        avatarUrl() {
            const root = this.$root;
            if (!root) return "";
            const u = root.utente || null;
            if (!u || !u.ID_UTENTE || !u.URL_AVATAR || u.URL_AVATAR === "placeholder.jpg") return "";
            const f = root.filters;
            return (f && typeof f.getImgPath === "function") ? f.getImgPath("USERS", u.URL_AVATAR) : "";
        },
        nomeVisibile() {
            const n = (this.nome || "").trim();
            return n || "Manager";
        },
        azioni() {
            return [{ nome: "Gestione progetti", rotta: "Progetti", icona: "ph-folder" }];
        },
    },

    methods: {
        vai(rotta) {
            if (this.$router && typeof this.$router.push === "function") {
                this.$router.push({ name: rotta });
            }
        },
    },
}
</script>

<style scoped>
.ui02-welcome {
    border: 1px solid #e9edf2;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, .05);
    overflow: hidden;
    flex: 1 1 auto;
    min-height: 250px;
    display: flex;
    flex-direction: column;
}
.ui02-welcome-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 10px 22px 10px;
    background: linear-gradient(120deg, #eef4ff 0%, #f4f9ff 100%);
    border-bottom: 1px solid rgba(13, 110, 253, .10);
}
.ui02-welcome-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: #fff;
    color: #0d6efd;
    font-size: 1.35rem;
    box-shadow: 0 2px 6px rgba(13, 110, 253, .18);
    flex-shrink: 0;
}
.ui02-welcome-avatar-img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
}
.ui02-welcome-title {
    display: flex;
    flex-direction: column;
    min-width: 0;
}
.ui02-welcome-saluto {
    font-size: .82rem;
    font-weight: 600;
    color: #334155;
}
.ui02-welcome-nome {
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 1.1;
    color: #0d6efd;
    overflow-wrap: anywhere;
}
.ui02-welcome-body {
    position: relative;
    flex: 1 1 auto;
    display: flex;
    align-items: center;
    padding: 20px 24px;
    overflow: hidden;
}
.ui02-welcome-text {
    position: relative;
    z-index: 1;
    max-width: 330px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.ui02-welcome-desc {
    font-size: .85rem;
    color: #64748b;
    margin: 0;
    max-width: 340px;
}
.ui02-welcome-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.ui02-welcome-action {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 12px;
    border: 1px solid transparent;
    border-radius: 10px;
    background-color: #e7f1ff;
    color: #0d6efd;
    font-size: .78rem;
    font-weight: 600;
    line-height: 1;
}
.ui02-welcome-action i {
    font-size: .9rem;
}
.ui02-welcome-action:hover {
    background-color: #d9e9ff;
    color: #0a58ca;
}
/* Decorazione sobria a destra del body (icone Phosphor + forme pastel). */
.ui02-welcome-art {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    width: 128px;
    height: 128px;
}
.ui02-welcome-art .ui02-art-bolla {
    position: absolute;
    border-radius: 50%;
    display: block;
}
.ui02-welcome-art .b1 { width: 96px; height: 96px; right: 0; top: 0; background: rgba(13, 110, 253, .08); }
.ui02-welcome-art .b2 { width: 64px; height: 64px; left: 0; bottom: 0; background: rgba(111, 66, 193, .07); }
.ui02-welcome-art .ui02-art-tile {
    position: absolute;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 11px;
    background: #fff;
    box-shadow: 0 5px 14px rgba(15, 23, 42, .10);
    color: #0d6efd;
    font-size: 1.05rem;
}
.ui02-welcome-art .t1 { right: 26px; top: 8px; }
.ui02-welcome-art .t2 { right: 78px; top: 40px; color: #8056d6; }
.ui02-welcome-art .t3 { right: 34px; bottom: 6px; color: #168a4c; }
/* Su viewport ridotte la decorazione e' nascosta (non deve comprimere). */
@media (max-width: 1199.98px) {
    .ui02-welcome-art {
        display: none;
    }
}
</style>